import { useParams, Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { articles, categories } from '@/mocks/data';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const [article, setArticle] = useState<typeof articles[0] | null>(null);
  const [related, setRelated] = useState<typeof articles>([]);

  useEffect(() => {
    const found = articles.find((a) => a.slug === slug);
    setArticle(found || null);

    if (found) {
      const relatedArticles = articles
        .filter((a) => a.category === found.category && a.slug !== found.slug)
        .slice(0, 3);
      setRelated(relatedArticles);
    }
  }, [slug]);

  useEffect(() => {
    if (!article) return;

    document.title = `${article.title} - 饭岛爱纪念网`;

    const setMeta = (name: string, content: string, isProperty = false) => {
      const attr = isProperty ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', article.excerpt);
    setMeta('keywords', article.tags.join(','));
    setMeta('og:title', article.title, true);
    setMeta('og:description', article.excerpt, true);
    setMeta('og:type', 'article', true);
    setMeta('og:url', `https://iijima-ai-memorial.com/article/${article.slug}`, true);
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', article.title);
    setMeta('twitter:description', article.excerpt);

    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', `https://iijima-ai-memorial.com/article/${article.slug}`);

    const existingScript = document.getElementById('article-jsonld');
    if (existingScript) existingScript.remove();

    const cat = categories.find((c) => c.slug === article.category);
    const jsonLd = {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: article.title,
      description: article.excerpt,
      image: article.image,
      datePublished: article.date,
      author: {
        '@type': 'Person',
        name: article.author,
      },
      publisher: {
        '@type': 'Organization',
        name: '饭岛爱纪念网',
      },
      articleSection: cat?.name || '',
    };

    const script = document.createElement('script');
    script.id = 'article-jsonld';
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(jsonLd);
    document.head.appendChild(script);
  }, [article]);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-file-unknow-line text-2xl text-foreground-400"></i>
            </div>
            <p className="text-foreground-500 text-lg mb-4">文章未找到</p>
            <Link to="/" className="text-sm text-primary-600 hover:text-primary-700 cursor-pointer">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const cat = categories.find((c) => c.slug === article.category);

  return (
    <>
      <Navbar />
      <main className="pt-16 min-h-screen">
        <article>
          <div className="relative w-full h-[350px] md:h-[450px] overflow-hidden">
            <img
              src={article.image}
              alt={article.title}
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/60"></div>
            <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10">
              <div className="max-w-4xl mx-auto">
                <div className="flex items-center gap-3 mb-3 flex-wrap">
                  {cat && (
                    <Link
                      to={`/category/${cat.slug}`}
                      className="text-xs px-3 py-1 rounded-full bg-white/20 text-white font-label hover:bg-white/30 transition-colors cursor-pointer"
                    >
                      {cat.name}
                    </Link>
                  )}
                  <span className="text-sm text-white/70">{article.date}</span>
                  <span className="text-sm text-white/70">·</span>
                  <span className="text-sm text-white/70">{article.author}</span>
                </div>
                <h1 className="font-heading text-2xl md:text-4xl font-bold text-white leading-tight">
                  {article.title}
                </h1>
              </div>
            </div>
          </div>

          <div className="max-w-4xl mx-auto px-4 md:px-6 py-8">
            <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-8 flex-wrap" aria-label="breadcrumb">
              <Link to="/" className="hover:text-foreground-800 transition-colors cursor-pointer">首页</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              {cat && (
                <>
                  <Link to={`/category/${cat.slug}`} className="hover:text-foreground-800 transition-colors cursor-pointer">
                    {cat.name}
                  </Link>
                  <i className="ri-arrow-right-s-line text-xs"></i>
                </>
              )}
              <span className="text-foreground-800 font-medium line-clamp-1">{article.title}</span>
            </nav>

            <div className="flex flex-wrap gap-2 mb-8">
              {article.tags.map((tag) => (
                <span key={tag} className="text-xs px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 font-label">
                  {tag}
                </span>
              ))}
            </div>

            <div className="prose-custom text-foreground-700 leading-relaxed text-base">
              {article.content.split('\n\n').map((paragraph, idx) => (
                <p key={idx} className="mb-5 leading-loose">
                  {paragraph}
                </p>
              ))}
            </div>

            <div className="border-t border-background-200 mt-10 pt-6">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-foreground-500">分享：</span>
                  <button className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-background-200 transition-colors cursor-pointer" aria-label="分享到微博">
                    <i className="ri-weibo-line text-sm"></i>
                  </button>
                  <button className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-background-200 transition-colors cursor-pointer" aria-label="分享到Twitter">
                    <i className="ri-twitter-x-line text-sm"></i>
                  </button>
                  <button className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-background-200 transition-colors cursor-pointer" aria-label="复制链接">
                    <i className="ri-link text-sm"></i>
                  </button>
                </div>
                <Link
                  to={cat ? `/category/${cat.slug}` : '/'}
                  className="text-sm text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1 cursor-pointer"
                >
                  <i className="ri-arrow-left-line"></i> 返回{cat?.name || '首页'}
                </Link>
              </div>
            </div>
          </div>
        </article>

        {related.length > 0 && (
          <section className="bg-background-100 py-14">
            <div className="max-w-4xl mx-auto px-4 md:px-6">
              <h2 className="font-heading text-2xl font-bold text-foreground-900 mb-8">相关文章</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {related.map((rel) => (
                  <Link
                    key={rel.id}
                    to={`/article/${rel.slug}`}
                    className="bg-background-50 rounded-lg overflow-hidden border border-background-200 hover:border-background-300 transition-all cursor-pointer group"
                  >
                    <div className="relative w-full h-40 overflow-hidden">
                      <img
                        src={rel.image}
                        alt={rel.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                    </div>
                    <div className="p-4">
                      <span className="text-xs text-foreground-400">{rel.date}</span>
                      <h3 className="font-heading text-sm font-semibold text-foreground-900 mt-1 line-clamp-2 group-hover:text-primary-600 transition-colors">
                        {rel.title}
                      </h3>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}